<template>
    <div>

        <form-select v-if="participationClassOptions.length > 1 || !selectedParticipationClass" v-model="data.participation_class_id"
                     :label="$t('label.participation_class')"
                     :help="$t('help.participation.participation_class_id')"
                     :name="`${validationPrefix}participation_class_id`"
                     :rules="{required: true}"
                     :color="event.color"
                     :placeholder="$t('placeholder.please_select')"
                     :options="participationClassOptions"
                     :disabled="disabled"/>

        <template v-if="selectedParticipationClass">
            <form-select v-if="registrationTypeOptions.length > 1" v-model="data.type"
                         :color="event.color"
                         :name="`${validationPrefix}registration_type`"
                         :label="$t('input.participation.registration_type')"
                         :placeholder="$t('placeholder.please_select')"
                         :disabled="disabled"
                         :rules="{required: true}"
                         :options="registrationTypeOptions"/>

            <template v-if="data.type">
                <lazy-form-club-select v-if="data.type === registrationTypes.Club" v-model="data.club_id"
                                       :color="event.color"
                                       :help="$t('help.participation.club_id')"
                                       :name="`${validationPrefix}club_id`"
                                       :disabled="disabled"
                                       v-model:selected="selectedClub"
                                       allow-create
									   :extended="[3288, 3287, 3590, 3664, 4240].includes(event.id)"
                                       :rules="{required: true}"
                                       :label="[3288, 3287, 3590, 3664, 4240].includes(event.id) ? 'Firma / Schule / Verein' : $t('label.club_company')"/>

                <lazy-form-team-select v-if="data.type === registrationTypes.Team" v-model="data.team_id"
                                       :disabled="disabled"
									   :event-id="event.id"
                                       :color="event.color"
                                       v-model:selected="selectedTeam"
                                       :name="`${validationPrefix}team_id`"
                                       :participation-class-id="data.participation_class_id"
                                       :force-private="selectedParticipationClass.team_settings?.required ?? false"
                                       allow-create
                                       :rules="{required: true}"
                                       :help="$t('text.participation.choose_participation_team')"
                                       :label="selectedParticipationClass.team_settings?.label ?? $t('label.squad')"/>

                <template v-if="data.type">
                    <expansion-panels v-model="openPanels">
                        <card v-if="displayCreatorForm" outlined>
                            <expansion-panel padding="px-8 sm:px-16"
                                             value="creator-form"
                                             :title="$t('label.registrant_information')" :description="$t('help.participation.registrant_data')">
                                <div class="px-8 sm:px-16">
                                    <row>
                                        <column cols="12" lg="6">
                                            <form-input v-model="creatorData.first_name"
                                                        :rules="{required: true, min: 2, max: 50}"
                                                        :disabled="disabled"
                                                        name="creator.first_name"
                                                        :label="$t('label.your_first_name')"/>
                                        </column>
                                        <column cols="12" lg="6">
                                            <form-input v-model="creatorData.last_name"
                                                        :label="$t('label.your_last_name')"
                                                        name="creator.last_name"
                                                        :rules="{required: true, min: 2, max: 50}"
                                                        :disabled="disabled"/>
                                        </column>
                                        <column cols="12">
                                            <form-input v-model="creatorData.email"
                                                        :label="$t('label.your_email')"
                                                        name="creator.email"
                                                        type="email"
                                                        :disabled="disabled"
                                                        :rules="{required: true, min: 4, max: 190}"/>
                                        </column>
                                    </row>
                                </div>
                            </expansion-panel>
                        </card>

                        <card outlined>
                            <expansion-panel v-for="(participation, participationIndex) in data.participations" :key="participationIndex"
                                             padding="px-8 sm:px-16"
                                             :value="`participation-form-${participationIndex}`"
                                             :title="getParticipationTitle(participationIndex)" :description="getParticipationDescription(participationIndex)">
                                <div class="px-8 sm:px-16">
                                    <participation-form-inputs :data="participation" :participation-class="selectedParticipationClass" :event="event"
                                                               :validation-prefix="validationPrefix?.length ? `${validationPrefix}participations.${participationIndex}.` : `participations.${(index+1) * participationIndex}.`"
                                                               :type="participationIndex > 0 && data.type === registrationTypes.Myself ? registrationTypes.Others : data.type"
                                                               :key="'participation-' + participationIndex" @active-price-changed="refreshActivePriceForParticipationGroup"/>

                                    <wrapper v-if="index > 0 || participationIndex > 0" class="mt-16">
                                        <btn outlined color="warning" @click="removeParticipation(participationIndex)" small>
                                            <icon :icon="icons.remove"/>
                                            <span>{{ $t('action.participant.remove') }}</span>
                                        </btn>
                                    </wrapper>
                                </div>
                            </expansion-panel>
                        </card>
                    </expansion-panels>
                </template>
            </template>
        </template>

        <wrapper v-if="index > 0 && (!data.type || !selectedParticipationClass)" class="mt-16">
            <btn outlined color="warning"
                 @click="removeParticipation(0)">
                <icon :icon="icons.remove"/>
                <span>{{ $t('action.participant.remove') }}</span>
            </btn>
        </wrapper>

        <btn v-if="data.type && selectedParticipationClass" outlined :color="event.color ? event.color : 'primary'"
			 :disabled="addParticipationDisabledReason !== null"
             @click="addParticipation" class="w-full">
            <icon :icon="icons.create"/>
			<span v-if="addParticipationDisabledReason">{{ addParticipationDisabledReason }}</span>
            <span v-else-if="data.type === registrationTypes.Club">{{ $t('action.add_club_member_in_participation_class') }}</span>
            <span v-else-if="data.type === registrationTypes.Team && selectedTeam">{{ $t('action.add_participant_to_name', {name: selectedTeam.name}) }}</span>
            <span v-else>{{ $t('action.add_participant_in_participation_class') }}</span>
        </btn>

        <validation-input :model-value="!validationErrorMessage" :error="validationErrorMessage"/>
        <lazy-alert v-if="validationErrorMessage" type="warning" class="mt-20">{{ validationErrorMessage }}</lazy-alert>

    </div>
</template>

<script>
import {FormInputsMixin} from "@spoferan/nuxt-spoferan/mixins";
import ParticipationRegistrationType from "@/models/ParticipationRegistrationType";
import {ParticipationInputState} from "@spoferan/spoferan-ts-core";
import {scrollToSelector} from "@spoferan/spoferan-ts-core";
import {getAnalyticsDataForParticipationClass} from "@spoferan/spoferan-ts-core";
import {createIcon, removeIcon} from "@spoferan/nuxt-spoferan/icons";

// The generic props that are entered for participations
const participationProps = [
    'first_name',
    'last_name',
    'email',
    'gender',
    'birthday',
    'phone',
    'emergency_phone',
    'country_code',
    'state_code',
    'postcode',
    'city',
    'street',
    'club_id'
];

export default {
    name: "ParticipationGroupFormInputs",

    mixins: [FormInputsMixin],

	emits: ['removed-participation'],

    props: {

        // The event to create the participation for
        event: {
            type: Object,
            required: true
        },

        // The participation classes the user can select for the participation
        participationClasses: {
            type: Array,
            required: true
        },

        // The data to fill the creator data
        creatorData: {
            type: Object,
            required: true
        },

        // The prefix to prepend on the input names, so validation errors on the backend can be assigned to the correct input field
        validationPrefix: {
            type: String,
            default: ''
        },

        // The index position at which the group inputs are displayed.
        index: {
            type: Number,
            default: 0
        }
    },

    data() {
        return {
            registrationTypes: ParticipationRegistrationType,
            openPanels: [],
            selectedTeam: null,
            selectedClub: null,

            // The registered watchers to sync the 'myself' participation data to the creator data
            participationDataToCreatorDataSyncWatchers: [],

			icons: {
				create: createIcon,
				remove: removeIcon
			}
        }
    },

    computed: {
        registrationTypeOptions() {
            const types = []

            // We only allow full team registrations when teams are enforced.
            // This option is never really selectable by the user as the select field is not displayed when only one option exists,
            // but simplifies our code.
            if (this.selectedParticipationClass?.team_settings?.required) {
                types.push({
                    value: ParticipationRegistrationType.Team,
                    text: this.selectedParticipationClass.team_settings?.label ?? this.$t(`input.participation.registration_type_options.${ParticipationRegistrationType.Team}`)
                })
                return types;
            }

            types.push({value: ParticipationRegistrationType.Myself, text: this.$t(`input.participation.registration_type_options.${ParticipationRegistrationType.Myself}`)})
            types.push({value: ParticipationRegistrationType.Others, text: this.$t(`input.participation.registration_type_options.${ParticipationRegistrationType.Others}`)});

            if (this.selectedParticipationClass?.team_settings) {
                types.push({
                    value: ParticipationRegistrationType.Team,
                    text: this.selectedParticipationClass.team_settings?.label ?? this.$t(`input.participation.registration_type_options.${ParticipationRegistrationType.Team}`)
                })
            }

            if (this.selectedParticipationClass && this.selectedParticipationClass.settings.club_input !== ParticipationInputState.Disabled) {
                types.push({
                    value: ParticipationRegistrationType.Club,
                    text: [3288, 3287, 3590, 3664, 4240].includes(this.event.id) ? 'Mehrere Mitglieder einer Firma / Schule / Verein' : this.$t(`input.participation.registration_type_options.${ParticipationRegistrationType.Club}`)
                })
            }

            return types;
        },

        participationClassOptions() {
            return this.participationClasses.map(participationClass => {
                const hasReachedParticipantsLimit = !!(participationClass.participants_count && participationClass.restrictions?.limit && (participationClass.restrictions.limit - participationClass.participants_count) <= 0);

                return {value: participationClass.id, text: this.getDisplayNameForParticipationClass(participationClass), disabled: hasReachedParticipantsLimit, subtitle: hasReachedParticipantsLimit ? this.$t('text.sold_out_participants_count') : null}
            })
        },

        groupTitle() {
            switch (this.data.type) {
                case ParticipationRegistrationType.Myself:
                case ParticipationRegistrationType.Others:
                    if (!this.data.participations[0]?.first_name || !this.data.participations[0]?.last_name) {
                        return null;
                    }

                    const title = `${this.data.participations[0].first_name} ${this.data.participations[0].last_name}`;

                    return this.data.participations.length > 1 ? `${title} (+${this.data.participations.length - 1})` : title;
                case ParticipationRegistrationType.Team:
                    return this.selectedTeam ? this.selectedTeam.name : null;
                case ParticipationRegistrationType.Club:
                    return this.selectedClub ? this.selectedClub.display_name : null;
            }

            return null;
        },

        selectedParticipationClass() {
            return this.data.participation_class_id ? this.participationClasses.find(participationClass => participationClass.id === this.data.participation_class_id) : null
        },

        displayCreatorForm() {
            return this.index === 0 && !!this.creatorData && this.data.type && this.data.type !== ParticipationRegistrationType.Myself && !this.$auth.user;
        },

        // An error message why the group can currently not be submitted that is not related to a single input.
        validationErrorMessage() {

            // Check if the min required team participants are specified
            if (this.data.type === ParticipationRegistrationType.Team && this.selectedParticipationClass?.team_settings?.min && this.selectedParticipationClass?.team_settings?.min > (this.data.participations.length + (this.selectedTeam?.participations_count ?? 0))) {
                return this.$t('restriction.min_limit', {count: this.selectedParticipationClass?.team_settings?.min, label: this.$t('label.participants')})
            }
            // Check if no more than the max team participations count is specified
            if (this.data.type === ParticipationRegistrationType.Team && this.selectedParticipationClass?.team_settings?.max && this.selectedParticipationClass?.team_settings?.max < (this.data.participations.length + (this.selectedTeam?.participations_count ?? 0))) {
                return this.$t('restriction.max_limit', {count: this.selectedParticipationClass?.team_settings?.max, label: this.$t('label.participants')})
            }

            return null;
        },

		addParticipationDisabledReason() {
			// Check if no more than the max team participations count is specified
			if (this.data.type === ParticipationRegistrationType.Team && this.selectedParticipationClass?.team_settings?.max && this.selectedParticipationClass?.team_settings?.max <= (this.data.participations.length + (this.selectedTeam?.participations_count ?? 0))) {
				return this.$t('restriction.max_limit', {count: this.selectedParticipationClass?.team_settings?.max, label: this.$t('label.participants')})
			}

			return null;
		}
    },

    watch: {
        registrationTypeOptions(options) {
            // Auto-select the first type option, if all options are initialized (participation class is set) and only one exists
            if (this.data.participation_class_id && options.length === 1) {
                this.data['type'] = options[0].value;
            }

            // If a type is set but the options changed, we need to ensure that the selected type still exists in the options to prevent update issues
            if (this.data.type && (!options.length || !options.find(option => option.value === this.data.type))) {
                this.data['type'] = null;
            }
        },

        'data.type'(type) {
            // Reset the participations when the type changes
            this.data['participations'] = [{
                participation_class_id: this.data.participation_class_id
            }]
            this.data['team_id'] = null
            this.data['club_id'] = null
            this.selectedTeam = null;
            this.selectedClub = null;
            this.openPanels = this.displayCreatorForm ? ['creator-form', 'participation-form-0'] : ['participation-form-0'];

            // Pre-fill the 'myself' data with the creator data if possible
            if (type === ParticipationRegistrationType.Myself && this.creatorData && this.index > 0) {
                // We need to wait for other potential watchers to be run, e.g., the club_id reset, to successfully set the required data
                this.$nextTick(function () {
                    this.fillMissingParticipationData(this.data.participations[0], this.creatorData);
                });
            }

            this.refreshParticipationDataToCreatorDataSync();
        },

        'data.participation_class_id'(participationClassId, oldParticipationClassId) {
            this.handleSelectedParticipationClass();

            if (oldParticipationClassId) {
                this.triggerAnalyticsCartEvent(oldParticipationClassId, false);
            }
            if (participationClassId) {
                this.triggerAnalyticsCartEvent(participationClassId);
            }
        },

        'data.team_id'() {
            this.setForAllParticipations('team_id', this.data.team_id);
        },

        'data.club_id'() {
            this.setForAllParticipations('club_id', this.data.club_id)
        },

        participationClasses() {
            this.setPreSelectedParticipationClass();
        },

        selectedTeam(selectedTeam) {
            // The team might has an impact on the group's price when the team has already been paid
            this.refreshActivePriceForParticipationGroup();

            // As a registration of all min team members in one process is required when the team is required (to charge a team amount),
            // we initially display the participation forms for all missing team participants
            if (selectedTeam && this.data.type === ParticipationRegistrationType.Team && this.selectedParticipationClass?.team_settings?.required) {
                const minParticipations = this.selectedParticipationClass.team_settings.min ?? 0;
                const additionalRequiredParticipations = minParticipations - this.data.participations.length - (selectedTeam.participations_count ?? 0);
                if (additionalRequiredParticipations > 0) {
                    for (let i = 0; i < additionalRequiredParticipations; i++) {
                        this.addParticipation(false);
                    }

                    // We open all participation panels, so the required data is instantly visible to the user
                    const participationPanels = [...Array(minParticipations).keys()].map(index => `participation-form-${index}`);
                    this.openPanels = this.displayCreatorForm ? ['creator-form', ...participationPanels] : participationPanels
                }
            }
        },

        groupTitle: {
            handler: function (title) {
                this.data['title'] = title
            },
            immediate: true
        }
    },

    created() {

        // Init the creator data if required
        if (this.index === 0 && this.creatorData && this.$auth.user) {
            this.fillMissingParticipationData(this.creatorData, this.$auth.user);
        }

        // Set up the watcher to fill the creator data with participation data
        this.refreshParticipationDataToCreatorDataSync();
    },

    mounted() {
        // This is required in the mounted hook to trigger the watcher accordingly.
        this.setPreSelectedParticipationClass();

        // Auto-select the first type option, if all options are initialized (participation class is set) and only one exists
        // This is required in the mounted hook to trigger the type watcher accordingly.
        if (this.data.participation_class_id && this.registrationTypeOptions.length === 1 && !this.data.type) {
            this.data['type'] = this.registrationTypeOptions[0].value;
        }
    },

    beforeDestroy() {
        this.unregisterParticipationDataToCreatorDataSync();
    },

    methods: {

        /**
         * Sets up the watchers on the participation props to fill the creator data with the 'myself' participation data, if required.
         */
        refreshParticipationDataToCreatorDataSync() {
            this.unregisterParticipationDataToCreatorDataSync();

            if (this.index === 0 && this.data.type === ParticipationRegistrationType.Myself && !this.$auth.user && this.creatorData) {
                participationProps.forEach(prop => {
                    this.participationDataToCreatorDataSyncWatchers.push(this.$watch(`data.participations.0.${prop}`, function (value) {
                        this.creatorData[prop] = value
                    }))
                })
            }
        },

        unregisterParticipationDataToCreatorDataSync() {
            this.participationDataToCreatorDataSyncWatchers.forEach(watcher => {
                watcher();
            })
            this.participationDataToCreatorDataSyncWatchers = [];
        },

        addParticipation(focus = true) {
            const newLength = this.data.participations.push({
                participation_class_id: this.data.participation_class_id,
                team_id: this.data.team_id,
                club_id: this.data.club_id,
            });

            if (focus) {
                this.openPanels = [`participation-form-${newLength - 1}`]; // Open the panel of the added participation
                setTimeout(() => {
                    scrollToSelector(`#participation-form-${newLength - 1}`)
                }, 500); // Wait until close animations are finished
            }

            // We only track analytics events for whole teams once (for the whole team)
            if (this.data.type !== ParticipationRegistrationType.Team || this.selectedParticipationClass?.team_settings?.fee_charge_mode !== 'team') {
                this.triggerAnalyticsCartEvent(this.data.participation_class_id);
            }
        },

        removeParticipation(index) {
            this.$confirm({
                title: this.$t('confirm.participation.remove.title'),
                text: this.$t('confirm.participation.remove.message', {name: this.getParticipationTitle(index)}),
                type: 'warning'
            }).then(({confirmed}) => {
                if (confirmed) {
					this.data.participations.splice(index, 1);

                    this.openPanels = [];
                    this.$emit('removed-participation', index);

                    // We only track analytics events for whole teams once (for the whole team)
                    if (this.data.type !== ParticipationRegistrationType.Team || this.selectedParticipationClass?.team_settings?.fee_charge_mode !== 'team') {
                        this.triggerAnalyticsCartEvent(this.data.participation_class_id, false);
                    }
                }
            })
        },

        getParticipationTitle(index) {
            const participation = this.data.participations[index];

            if (this.data.type === ParticipationRegistrationType.Myself && index === 0) {
                return participation.first_name && participation.last_name ? `${participation.first_name} ${participation.last_name}` : this.$t('label.my_data')
            }

            return participation.first_name && participation.last_name ? `${participation.first_name} ${participation.last_name}` : `${this.$t('label.participant')} ${index + 1}`
        },

        getParticipationDescription(index) {
            if (this.data.type === ParticipationRegistrationType.Myself && index === 0) {
                return this.$t('help.participation.your_data')
            }

            if (this.selectedTeam) {
                return this.$t('help.participation.team_participant_data', {index: index + 1, team: this.selectedTeam.name})
            }

            return this.$t('help.participation.participant_data', {index: index + 1})
        },

        /**
         * Checks if a pre-chosen participation class or only one class exists -> select for participations
         */
        setPreSelectedParticipationClass() {
            if (!this.data.participation_class_id) {
                let preSelectedClassId = undefined // Undefined to prevent triggering validation checks on participation class input

                // We only want to pre-select classes that are not disabled
                if (this.participationClasses.length === 1) {
                    preSelectedClassId = this.participationClassOptions.find(option => !option.disabled)?.value
                } else if (this.$route.query.class) {
                    const preSelectedClass = this.participationClasses.find(participationClass => participationClass._key === this.$route.query.class);
                    if (preSelectedClass && this.participationClassOptions.find(option => !option.disabled && option.value === preSelectedClass.id)) {
                        preSelectedClassId = preSelectedClass.id;
                    }
                }
                this.data['participation_class_id'] = preSelectedClassId
            }
        },

        handleSelectedParticipationClass() {
            this.setForAllParticipations('participation_class_id', this.data.participation_class_id)
            this.data['subtitle'] = this.getDisplayNameForParticipationClass(this.selectedParticipationClass)
            this.refreshActivePriceForParticipationGroup();

            // The teams are related to the participation class, so we need to reset them
            this.data['team_id'] = null;
            this.selectedTeam = null;
        },

        setForAllParticipations(propName, propValue) {
            this.data.participations.forEach(participation => {
                participation[propName] = propValue
            })
        },

        getDisplayNameForParticipationClass(participationClass) {
            if (!participationClass) {
                return null;
            }

            const primaryRaces = participationClass.stageRaces.filter(race => race.register_participation_class_id === participationClass.id);

            return primaryRaces.length ? primaryRaces.map(race => race.name).join(' + ') : participationClass.name
        },

        /**
         * Sets the missing participation props on the target object based on the data of the source object.
         *
         * @param {object} target
         * @param {object} source
         */
        fillMissingParticipationData(target, source) {
            participationProps.forEach(prop => {
                target[prop] = target[prop] ? target[prop] : source[prop]
            })
        },

        /**
         * Updates the currently active price for the participation group in its participation class.
         */
        refreshActivePriceForParticipationGroup() {
            this.data['activePrice'] = this.findActivePrice(this.selectedParticipationClass)
        },

        /**
         * Gets the active price for the entered participation data and the selected participation class.
         */
        findActivePrice(participationClass, forAnalytics = false) {
            // Prices only belong to the group when teams are required
            // Single participation fees are set on the ParticipationFormInputs component
            if (!participationClass || (!forAnalytics && participationClass?.team_settings?.fee_charge_mode !== 'team')) {
                return null;
            }

            // If the team has an order_id, then a team participation fee was already paid whereby all additional participations are for free
            if (this.selectedTeam?.order_id) {
                return forAnalytics ? null : {
                    cents: 0
                };
            }

			const compareDate = participationClass.start_date && participationClass.start_date ? this.$date(participationClass.start_date) : this.$date()
            const prices = participationClass.prices
            if (prices && prices.length) {
				const activeAgePrices = prices.filter(price => {
					const allParticipationsInAge = (!price.min_age && !price.max_age) || this.data.participations.every(participation => {
						return ((!participation.birthday) || ((!price.min_age || compareDate.diff(participation.birthday, 'years') >= price.min_age) && (!price.max_age || compareDate.diff(participation.birthday, 'years') <= price.max_age)))
					});

					return price.is_active && !price.for_additional && allParticipationsInAge;
				})

                if (activeAgePrices && activeAgePrices.length) {

                    // The lowest active price will be taken by default
                    return activeAgePrices.sort((a, b) => a.cents < b.cents ? -1 : 1)[0]
                }
            }

            return null;
        },

        triggerAnalyticsCartEvent(participationClassId, isAddToCart = true) {
            const participationClass = this.participationClasses.find(participationClass => participationClass.id === participationClassId);
            if (!participationClass) {
                return;
            }
            const activePrice = this.findActivePrice(participationClass, true);
            if (!activePrice) {
                return;
            }

            this.$analytics.trackEvent(isAddToCart ? 'add_to_cart' : 'remove_from_cart', {
                currency: 'EUR',
                value: activePrice.cents !== null ? (Math.round((activePrice.cents / 100) * 100) / 100) : null,
                items: [getAnalyticsDataForParticipationClass(this.event, participationClass, 1, activePrice.cents)]
            });
        }
    }
}
</script>